import axios from 'axios'
import React, { useEffect, useState } from 'react';
import { Office } from '../../../models/office';
import Title from '../../../components/Title';
import { NavLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import StickyListToolbar, {
    stickyListRegionWidth,
} from '../../../components/layouts/StickyListToolbar';
import { useDrawer } from '../../../components/context/DrawerContext';
import EmptyStateMessage from '../../../components/layouts/EmptyStateMessage';
import { Card, CardContent } from '@mui/material';
import SimpleCustomTable from '../../../components/table/SimpleCustomTable';
import {
    iconActionColumnSizing,
    simpleTableIconButtonSx,
} from '../../../components/table/iconActionColumnSizing';
import { SimpleColumn } from '../../../components/table/interface';
import PermissionButton from '../../../components/common/PermissionButton';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';

interface ShowAlerts {
    showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
    showLoading: () => void;
    hideLoading: () => void;
}

const Offices: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
    const [offices, setOffices] = useState<Office[]>([]);
    const { drawerOpen } = useDrawer();
    const theme = useTheme();
    const { canCreate, canUpdate, canDelete } = useResourcePermissions('/administrar/oficinas');

    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    document.title = "Administrar Oficinas"
    useEffect(() => {
        const fetchData = async () => {
            showLoading();
            try {
                const { data } = await axios.get('alloffices');
                const list = Array.isArray(data.data) ? data.data : [];
                const sorted = [...list].sort((a: Office, b: Office) =>
                    String(a?.name ?? '').localeCompare(String(b?.name ?? ''), 'es', {
                        sensitivity: 'base',
                    }),
                );
                setOffices(sorted);
                await new Promise((resolve) => setTimeout(resolve, 1000));
                showAlert('Se ha realizado la consulta exitosamente!', 'info');
            } catch (error) {
                showAlert('Error recuperando las oficinas!', 'error');
            } finally {
                hideLoading();
            }
        };
        fetchData();
    }, []);

    const simpleColumns: SimpleColumn[] = [
        { field: 'name', headerName: 'Nombre', minWidth: 100, maxWidth: 150 },
        { field: 'address', headerName: 'Dirección', minWidth: 150, maxWidth: 150 },
        { field: 'country', headerName: 'País', minWidth: 150, maxWidth: 150 },
        { field: 'city', headerName: 'Ciudad', minWidth: 150, maxWidth: 150 },
        { field: 'phone', headerName: 'Celular', minWidth: 150, maxWidth: 150 },

        // Opciones: Editar (columna individual)
        {
            field: 'opt_edit',
            headerName: '',
            ...iconActionColumnSizing(isMobile),
            renderCell: ({ row }) => (
                <PermissionButton
                    allowed={canUpdate}
                    deniedTooltip="Sin permiso para editar"
                    tooltip="Editar"
                    variant="contained"
                    color="primary"
                    sx={simpleTableIconButtonSx}
                    component={NavLink}
                    to={`/administrar/oficinas/${row.id}/editar`}
                >
                    <EditIcon fontSize="small" />
                </PermissionButton>
            ),
        },

        // Opciones: Eliminar (columna individual)
        {
            field: 'opt_delete',
            headerName: '',
            ...iconActionColumnSizing(isMobile),
            renderCell: ({ row }) => (
                <PermissionButton
                    allowed={canDelete}
                    deniedTooltip="Sin permiso para eliminar"
                    tooltip="Eliminar"
                    variant="contained"
                    color="error"
                    sx={simpleTableIconButtonSx}
                    onClick={() => handleDeleteOffice(row.id)}
                >
                    <DeleteIcon fontSize="small" />
                </PermissionButton>
            ),
        }

    ];


    const rows = offices.map((office: Office) => ({
        ...office,
        name: office.name,
        phone: office.phone,
        address: office.address,
        id: office.id,
        country: office.type_country?.name,
        city: office.city,
    }));
    const handleDeleteOffice = async (id: number | string) => {
        if (!canDelete) return;

        const confirm = window.confirm(
            '¿Estás seguro que deseas eliminar esta oficina?\nSe eliminarán también todos los registros asociados (cobros, cajas, usuarios, cierres, etc.).'
        );

        if (!confirm) return;

        showLoading();

        try {
            await axios.delete(`/offices/${id}`);
            setOffices((prev) => prev.filter((office: Office) => office.id !== id));
            showAlert('Oficina eliminada correctamente', 'success');
        } catch (error: any) {
            console.error('Error al eliminar la oficina:', error);
            showAlert(
                error?.response?.data?.message || 'Error al eliminar la oficina',
                'error'
            );
        } finally {
            hideLoading();
        }
    };

    return (
        <HeaderResponsive>
            <StickyListToolbar>
                <Title>Información Oficinas</Title>
                <Box sx={{ ml: 'auto' }}>
                    <PermissionButton
                        allowed={canCreate}
                        deniedTooltip="Sin permiso para crear"
                        tooltip="Agregar"
                        variant="contained"
                        startIcon={isMobile ? null : <AddIcon />}
                        component={NavLink}
                        to="/administrar/oficinas/add"
                    >
                        {isMobile ? <AddIcon /> : 'Agregar'}
                    </PermissionButton>
                </Box>
            </StickyListToolbar>
            <Card>
                <CardContent
                    sx={{
                        width: (theme) => stickyListRegionWidth(theme, drawerOpen),
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        overflowX: 'auto',
                    }}
                >
                    {offices.length > 0 ? (
                        <SimpleCustomTable
                            rows={rows}
                            columns={simpleColumns}
                            maxHeight={600}
                            //rowHeight={24}
                            showBorders
                            showCount
                            wrapCellContent
                            exportCsv
                            exportFileName="oficinas.csv"
                            columnGroups={[{ header: 'Opciones', fields: ['opt_edit', 'opt_delete'] }]}
                        />
                    ) : (
                        <EmptyStateMessage message="No existen registros" align="left" />
                    )}
                </CardContent>
            </Card>
        </HeaderResponsive>
    );
};

export default Offices;
